import Text from '@cartwhl/ui/base/TextNew';
import Button from '@cartwhl/ui/input/Button';

import { useStore } from '../dashboardStore';
import type { DashboardWorkFilter } from '../types';
import { groupDashboardWorkItems } from '../workItemGrouping';
import DashboardLibraryPrompt from './DashboardLibraryPrompt';
import DashboardWorkListLoading from './DashboardWorkListLoading';
import DashboardWorkSection from './DashboardWorkSection';

interface DashboardWorkListProps {
  workFilter: DashboardWorkFilter;
  onGoToGettingStarted: () => void;
}

export default function DashboardWorkList({
  workFilter,
  onGoToGettingStarted,
}: DashboardWorkListProps) {
  const dashboardStore = useStore();
  const { workItemsLoad, workItemsSearchQuery, workItemsTypeFilter } =
    dashboardStore.state;
  const workItems = workItemsLoad.data?.items ?? [];

  if (workItemsLoad.status === 'initial') {
    return null;
  }

  if (workItemsLoad.status === 'pending' && !workItemsLoad.data) {
    return <DashboardWorkListLoading />;
  }

  const normalizedSearchQuery = workItemsSearchQuery.trim().toLowerCase();
  const filteredWorkItems = workItems.filter((item) => {
    if (item.scope !== workFilter) return false;
    if (workItemsTypeFilter !== 'all' && item.kind !== workItemsTypeFilter) {
      return false;
    }
    if (!normalizedSearchQuery) return true;

    return item.title.toLowerCase().includes(normalizedSearchQuery);
  });
  const sections = groupDashboardWorkItems(filteredWorkItems);

  if (sections.length === 0) {
    const hasItemsForTab = workItems.some((item) => item.scope === workFilter);
    const hasActiveFilters =
      Boolean(normalizedSearchQuery) || workItemsTypeFilter !== 'all';

    if (hasItemsForTab && hasActiveFilters) {
      return (
        <Text token="14/medium" className="text-secondary">
          No matching work found.
        </Text>
      );
    }

    return (
      <div className="flex flex-col items-center justify-center gap-4 py-10">
        <Text token="14" className="text-tertiary pb-1 text-center text-pretty">
          Your recent work will appear. Need help getting started?
        </Text>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={onGoToGettingStarted}
        >
          Go to Getting Started
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      {sections.map((section, index) => (
        <DashboardWorkSection
          key={section.id}
          section={section}
          showDivider={index > 0}
        />
      ))}
      <DashboardLibraryPrompt />
    </div>
  );
}
